// ft3d-oa-threat.ts — BG3-style opportunity-attack threat indicator (frontend-only), carved out of the
// integration tail (extracted to includes/js/ft3d-oa-threat.js) and TS-ported for Phase 2a of
// plans/bundler-script-load-order-phase2.md — the third Wave-2a fold into the post-template-tail aggregate.
//
// While the user is dragging an encounter creature, mark any hostile creature whose reach the preview path
// would cross with: (1) a pulsing red outline on the threatening token, (2) a dashed red SVG "threat line"
// from the hostile to the mover preview with a sword icon at the arc peak, and (3) a red dashed outline on
// the drop preview. Driven from DragManager via window.FT3D_OA_THREAT.{clear,update}.
//
// MODULE-SCOPE SAFE: the only eval-time work is the one-time <style> injection (pure DOM, no template.js
// deps) and a `typeof`-guarded `_dbg('module init')`. Its template.js dependencies — isOpposingTeamPair
// (typeof-guarded), ftPosCoord, window.ftEncounterField (optional-chained), ftDebugLog (typeof-guarded) —
// are all reached inside drag-time handlers, never as unguarded module-scope reads → clean 2a fold. The
// original was already an IIFE, so all internals stay module-private here too.
//
// PUBLIC GLOBAL (window-attached at the bottom): FT3D_OA_THREAT — read by the encounter-map bundle's
// DragManager / TokenDragController at drag time.

import { encounterCreatureField, encounterField, reachOf } from '../shared/array-types';

interface GridPoint {
    x: number;
    z: number;
}

interface ThreatWidget {
    encounterArr?: unknown;
    elements?: { canvas?: HTMLElement | null };
    isWithinReach(hostile: unknown, mover: unknown): boolean;
    pixelFromGridX(x: number): number;
    pixelFromGridZ(z: number): number;
}

interface ThreatCandidate {
    id: string;
    creatureObj: unknown;
}

declare global {
    interface Window {
        FT3D_OA_THREAT: {
            update(widget: ThreatWidget, moverObj: unknown, origin: GridPoint, preview: GridPoint):
                { threatActive: boolean; snap: GridPoint | null };
            clear(): void;
            computeCandidates(widget: ThreatWidget, moverObj: unknown): ThreatCandidate[];
        };
    }
}

// ────────────── one-time style injection ──────────────
if (!document.getElementById('ft3d-oa-threat-style')) {
    const style = document.createElement('style');
    style.id = 'ft3d-oa-threat-style';
    style.textContent = `
@keyframes oa-threat-glow {
    0%, 100% { box-shadow: 0 0 6px 1px rgba(255,48,48,0.45); }
    50%      { box-shadow: 0 0 16px 4px rgba(255,48,48,0.85); }
}
.encounter-creature.oa-threat-active {
    outline: 2px solid #ff3030 !important;
    outline-offset: 2px;
    animation: oa-threat-glow 1.2s ease-in-out infinite;
    z-index: 1000;
}
.drop-preview.oa-threat-pending {
    outline: 2px dashed #ff3030 !important;
    outline-offset: 2px;
    background-color: rgba(255, 48, 48, 0.15) !important;
}
.ft3d-oa-threat-svg {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    pointer-events: none;
    overflow: visible;
    /* Above every creature token (DragManager._creatureBaseZFromDatasetSize tops
       out at 800 for tiny) but below modals / drag-feedback layers. */
    z-index: 2000;
}
.ft3d-oa-threat-svg .threat-line {
    stroke: #ff3030;
    stroke-width: 2.5;
    stroke-dasharray: 8 5;
    opacity: 0.9;
    fill: none;
    filter: drop-shadow(0 0 2px rgba(0,0,0,0.7));
}
.ft3d-oa-threat-svg .threat-icon-sword {
    /* Layered halo for contrast on any background: thin black outline +
       bright yellow glow. The yellow against the red line maximizes pop. */
    filter:
        drop-shadow(0 0 1px #000)
        drop-shadow(0 0 1px #000)
        drop-shadow(0 0 6px #ffd700)
        drop-shadow(0 0 10px rgba(255, 215, 0, 0.6));
    animation: oa-threat-icon-pulse 1.2s ease-in-out infinite;
}
@keyframes oa-threat-icon-pulse {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.18); }
}
`;
    document.head.appendChild(style);
}

// ────────────── module state ──────────────
let _candidates: ThreatCandidate[] | null = null;
let _candidatesForMoverId: string | null = null;
let _prevThreatIds = new Set<string>();

// ────────────── diagnostics ──────────────
function _dbg(label: string, payload: Record<string, unknown>): void {
    if (typeof ftDebugLog === 'function') {
        ftDebugLog('ft3d-oa-threat: ' + label, () => payload);
    }
}

// ────────────── accessors ──────────────
// FIXED 2026-06-26: the live encounter-map rows are full query_d_item_arr arrs exposing the CURRENT
// `encounter_creatures` (plural, bare scalars) shape — what token-data.ts / TokenDragController / team-filters
// read. The previous accessors read ONLY the legacy `encounter_creature_arr` (singular, [0]-wrapped) shape,
// which no current builder produces → every _id/_team came back '' → computeCandidates always returned []
// (OA threat was a silent no-op). Read the current shape, keep the legacy as a fallback for any warm blob.
function _record(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function _creatures(widget: ThreatWidget): unknown[] {
    const encounter = _record(widget.encounterArr);
    return Array.isArray(encounter?.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [];
}

function _ec(c: unknown, key: string): unknown {
    const creature = _record(c);
    const v = encounterCreatureField(creature?.encounter_creatures, key);
    if (v !== undefined && v !== null && v !== '') return v;
    const legacy = _record(creature?.encounter_creature_arr);
    const leg = legacy?.[key];
    return Array.isArray(leg) ? leg[0] : leg;
}
const _id        = (c: unknown): string  => String(_ec(c, 'encounter_creature_id') ?? '');
const _team      = (c: unknown): string  => String(_ec(c, 'team') ?? '');
const _isAlive   = (c: unknown): boolean => _ec(c, 'encounter_creature_dead') != 1;
const _reaction  = (c: unknown): boolean => parseInt(String(_ec(c, 'reaction') ?? 0), 10) === 1;
// reachOf reads computed.creature.reach (reach moved off the top-level slot 2026-05-30); default 5 ft when
// absent. Explicit 0 still excludes truly ranged-only creatures via the `_reach(c) <= 0` gate below. The old
// `c.reach?.[0]` read the dead legacy slot → always 5.
const _reach     = (c: unknown): number  => reachOf(c) ?? 5;
const _inCombat  = (w: ThreatWidget): boolean => parseInt(String(encounterField(w.encounterArr, 'in_combat') ?? 0), 10) === 1;

// ────────────── candidate gating ──────────────
function computeCandidates(widget: ThreatWidget, moverObj: unknown): ThreatCandidate[] {
    if (!_inCombat(widget)) return [];
    const arr = _creatures(widget);
    const moverId = _id(moverObj);
    const moverTeam = _team(moverObj);
    const opposing: (a: string, b: string) => boolean =
        (typeof isOpposingTeamPair === 'function') ? isOpposingTeamPair : () => false;
    const out: ThreatCandidate[] = [];
    for (const c of arr) {
        if (!c) continue;
        if (_id(c) === moverId) continue;
        if (!_isAlive(c)) continue;
        if (!_reaction(c)) continue;
        if (_reach(c) <= 0) continue;
        if (!opposing(moverTeam, _team(c))) continue;
        out.push({ id: _id(c), creatureObj: c });
    }
    return out;
}

// ────────────── path geometry ──────────────
// Synthesize a duck-typed mover at (x,z) for footprint geometry. We only override the position; size/picture metadata is preserved.
function _syntheticMoverAt(moverObj: unknown, x: number, z: number): Record<string, unknown> {
    const mover = _record(moverObj) ?? {};
    return Object.assign({}, mover, {
        position: Object.assign({}, _record(mover.position) ?? {}, {
            x_pos: x,
            z_pos: z,
        }),
    });
}

// Corrected 2026-07-19 (same unit error the overrun sibling carried): despite the "grid"
// naming, origin/preview are already in FEET — they are the drag controller's gridX/gridZ,
// fed verbatim to x_pos via _syntheticMoverAt() and compared against reach in feet. The old
// `* FEET_PER_GRID` inflated the path length 5x, so the intended max(2.5, reach/2) spacing
// actually sampled 5x finer than designed.
function _segmentSamples(origin: GridPoint, preview: GridPoint, hostileReachFt: number): GridPoint[] {
    const dx = preview.x - origin.x;
    const dz = preview.z - origin.z;
    const pathFt = Math.hypot(dx, dz);
    // Half the hostile's reach — fine enough that a reach band can never be stepped over.
    const stepFt = Math.max(2.5, hostileReachFt / 2);
    const n = Math.max(2, Math.ceil(pathFt / stepFt) + 1);
    const pts: GridPoint[] = new Array(n);
    for (let i = 0; i < n; i++) {
        const t = i / (n - 1);
        pts[i] = { x: origin.x + dx * t, z: origin.z + dz * t };
    }
    return pts;
}

function _threatensMover(widget: ThreatWidget, hostileObj: unknown, moverObj: unknown, origin: GridPoint, preview: GridPoint): boolean {
    const samples = _segmentSamples(origin, preview, _reach(hostileObj));
    let everInReach = false;
    let endpointInReach = false;
    for (let i = 0; i < samples.length; i++) {
        const sample = samples[i];
        if (!sample) continue;
        const syn = _syntheticMoverAt(moverObj, sample.x, sample.z);
        const inReach = widget.isWithinReach(hostileObj, syn);
        if (inReach) everInReach = true;
        if (i === samples.length - 1) endpointInReach = inReach;
    }
    return everInReach && !endpointInReach;
}

// ────────────── token decoration ──────────────
// Per-creature signal: outline + pulsing glow via CSS class on the OUTER
// creature token. We intentionally do NOT inject anything into
// .encounter-creature-overlay because that overlay is hover-gated
// (opacity:0 unless .encounter-creature:hover — see style.css:52077),
// and the user can't hover the threatening creature during a drag.
function _decorate(id: string, on: boolean): void {
    const el = document.getElementById('creature_' + id);
    if (!el) return;
    if (on) {
        el.setAttribute('data-oa-threat', '1');
        el.classList.add('oa-threat-active');
    } else {
        el.removeAttribute('data-oa-threat');
        el.classList.remove('oa-threat-active');
    }
}

// ────────────── SVG threat lines ──────────────
// BG3-style cue: dashed red line from each threatening hostile to the
// mover's preview position, with an inline-SVG sword icon hanging just
// above the midpoint. Rendered inside the active geometry host's canvas so all
// positioning uses canvas pixel coordinates from widget.pixelFromGridX/Y.
const SVG_NS = 'http://www.w3.org/2000/svg';

function _ensureThreatSvg(widget: ThreatWidget): SVGElement | null {
    const canvas = widget?.elements?.canvas;
    if (!canvas) return null;
    let svg = canvas.querySelector<SVGElement>(':scope > .ft3d-oa-threat-svg');
    if (!svg) {
        svg = document.createElementNS(SVG_NS, 'svg');
        svg.setAttribute('class', 'ft3d-oa-threat-svg');
        // <defs> with a single shared arrowhead marker.
        const defs = document.createElementNS(SVG_NS, 'defs');
        const marker = document.createElementNS(SVG_NS, 'marker');
        marker.setAttribute('id', 'ft3d-oa-arrowhead');
        marker.setAttribute('viewBox', '0 0 10 10');
        marker.setAttribute('refX', '9');
        marker.setAttribute('refY', '5');
        marker.setAttribute('markerWidth', '6');
        marker.setAttribute('markerHeight', '6');
        marker.setAttribute('orient', 'auto-start-reverse');
        const arrow = document.createElementNS(SVG_NS, 'path');
        arrow.setAttribute('d', 'M0,0 L10,5 L0,10 Z');
        arrow.setAttribute('fill', '#ff3030');
        marker.appendChild(arrow);
        defs.appendChild(marker);
        svg.appendChild(defs);
        canvas.appendChild(svg);
    }
    return svg;
}

function _removeThreatSvg(): void {
    document.querySelectorAll('.ft3d-oa-threat-svg').forEach((s) => s.remove());
}

function _renderThreatLines(widget: ThreatWidget, threatList: ThreatCandidate[], previewGrid: GridPoint): void {
    const svg = _ensureThreatSvg(widget);
    if (!svg) return;

    // Wipe prior content but preserve <defs>.
    const defs = svg.querySelector(':scope > defs');
    svg.innerHTML = '';
    if (defs) svg.appendChild(defs);

    if (!threatList || threatList.length === 0) return;

    const moverPx = widget.pixelFromGridX(previewGrid.x);
    const moverPy = widget.pixelFromGridZ(previewGrid.z);

    for (const t of threatList) {
        const hx = parseFloat(String(ftPosCoord(t.creatureObj, 'x_pos') ?? 0));
        const hz = parseFloat(String(ftPosCoord(t.creatureObj, 'z_pos') ?? 0));
        const hostilePx = widget.pixelFromGridX(hx);
        const hostilePy = widget.pixelFromGridZ(hz);

        // Curved threat path: arcs perpendicular through a control point
        // offset above the midpoint, so it visually separates from the
        // straight drag-line that also terminates at the mover preview.
        const midX = (hostilePx + moverPx) / 2;
        const midY = (hostilePy + moverPy) / 2;
        const lineDx = moverPx - hostilePx;
        const lineDy = moverPy - hostilePy;
        const lineLen = Math.hypot(lineDx, lineDy) || 1;
        // Perpendicular pointing "upward" (negative-y) — pick the perp
        // whose y component is most negative so the arc always bows up.
        let perpX = -lineDy / lineLen;
        let perpY = lineDx / lineLen;
        if (perpY > 0) { perpX = -perpX; perpY = -perpY; }
        const ARC_OFFSET_PX = 28;
        const ctrlX = midX + perpX * ARC_OFFSET_PX;
        const ctrlY = midY + perpY * ARC_OFFSET_PX;

        const path = document.createElementNS(SVG_NS, 'path');
        path.setAttribute('class', 'threat-line');
        path.setAttribute('d',
            `M ${hostilePx} ${hostilePy} Q ${ctrlX} ${ctrlY} ${moverPx} ${moverPy}`
        );
        path.setAttribute('marker-end', 'url(#ft3d-oa-arrowhead)');
        svg.appendChild(path);

        // Sword icon sits at the arc peak (same perpendicular offset as the
        // bezier control point), so the line visually passes through it.
        // Outer <g> anchors the position; inner <g> takes the CSS scale
        // pulse without clobbering the translate.
        const iconAnchor = document.createElementNS(SVG_NS, 'g');
        iconAnchor.setAttribute('transform', `translate(${ctrlX},${ctrlY})`);
        const iconPulse = document.createElementNS(SVG_NS, 'g');
        iconPulse.setAttribute('class', 'threat-icon-sword');
        // Inline sword silhouette: vertical blade up, hilt down, centered at (0,0).
        const sword = document.createElementNS(SVG_NS, 'path');
        sword.setAttribute('d',
            'M-1,-12 L1,-12 L1,4 L4,4 L4,5.5 L1,5.5 L1,9 L-1,9 L-1,5.5 L-4,5.5 L-4,4 L-1,4 Z'
        );
        // Yellow blade with dark outline — high contrast vs. the red arc
        // and vs. typical map backgrounds. Glow filter handled in CSS.
        sword.setAttribute('fill', '#ffd700');
        sword.setAttribute('stroke', '#1a1a00');
        sword.setAttribute('stroke-width', '0.8');
        iconPulse.appendChild(sword);
        iconAnchor.appendChild(iconPulse);
        svg.appendChild(iconAnchor);
    }
}

// ────────────── snap geometry ──────────────
// For each candidate currently threatening (preview is outside its reach but
// some point of the path was inside), binary-search the exact reach-boundary
// crossing fraction t∈[0,1] along the segment. If the preview lies within
// SNAP_BAND_FT past that crossing, mark this candidate as a snap source.
// Snap to exactly SNAP_BAND_FT past the FARTHEST such crossing so the
// preview lands deterministically outside every near-boundary threat.
const SNAP_BAND_FT = 3;

// Corrected 2026-07-19: origin/preview are in FEET (see _segmentSamples above), so the old
// `segLenGrid * FEET_PER_GRID` / `... / FEET_PER_GRID` pair was a round-trip through a unit
// this space never used. The two mostly cancelled, but SNAP_BAND_FT did not survive the trip:
// the snap landed SNAP_BAND_FT/5 = 0.6 ft past the reach crossing instead of the intended
// 3 ft, so the preview could still sit inside a hostile's reach band.
function _computeSnap(widget: ThreatWidget, moverObj: unknown, origin: GridPoint, preview: GridPoint): GridPoint | null {
    if (!_candidates || _candidates.length === 0) return null;
    const dxg = preview.x - origin.x;
    const dzg = preview.z - origin.z;
    const segLenFt = Math.hypot(dxg, dzg);
    if (segLenFt < 0.01) return null;
    const dirX = dxg / segLenFt;
    const dirZ = dzg / segLenFt;

    let farthestExitFt = -1;
    for (const cand of _candidates) {
        const previewSyn = _syntheticMoverAt(moverObj, preview.x, preview.z);
        if (widget.isWithinReach(cand.creatureObj, previewSyn)) continue; // preview still inside
        const originSyn = _syntheticMoverAt(moverObj, origin.x, origin.z);
        const originIn = widget.isWithinReach(cand.creatureObj, originSyn);

        let lo = 0, hi = 1;
        if (!originIn) {
            // Origin already outside: coarse-scan forward to find first-in.
            let firstIn = -1;
            const SCAN = 16;
            for (let s = 1; s <= SCAN; s++) {
                const t = s / SCAN;
                const syn = _syntheticMoverAt(moverObj,
                    origin.x + dxg * t, origin.z + dzg * t);
                if (widget.isWithinReach(cand.creatureObj, syn)) { firstIn = t; break; }
            }
            if (firstIn < 0) continue; // path never entered reach
            lo = firstIn;
        }
        // Bisect: invariant is reach(lo) === in, reach(hi) === out.
        for (let it = 0; it < 12; it++) {
            const mid = (lo + hi) / 2;
            const syn = _syntheticMoverAt(moverObj,
                origin.x + dxg * mid, origin.z + dzg * mid);
            if (widget.isWithinReach(cand.creatureObj, syn)) lo = mid; else hi = mid;
        }
        const exitFracT = hi;
        const exitFt = exitFracT * segLenFt;
        if (segLenFt - exitFt < SNAP_BAND_FT) {
            if (exitFt > farthestExitFt) farthestExitFt = exitFt;
        }
    }

    if (farthestExitFt < 0) return null;
    const snapDistFt = farthestExitFt + SNAP_BAND_FT;
    return {
        x: origin.x + dirX * snapDistFt,
        z: origin.z + dirZ * snapDistFt,
    };
}

// ────────────── public API ──────────────
function update(widget: ThreatWidget, moverObj: unknown, origin: GridPoint, preview: GridPoint):
    { threatActive: boolean; snap: GridPoint | null } {
    if (!widget || !moverObj || !origin || !preview) {
        clear();
        return { threatActive: false, snap: null };
    }

    const moverId = _id(moverObj);
    if (_candidates === null || _candidatesForMoverId !== moverId) {
        _candidates = computeCandidates(widget, moverObj);
        _candidatesForMoverId = moverId;
        _dbg('candidates', { count: _candidates.length, ids: _candidates.map((c) => c.id) });
    }

    _dbg('update', {
        moverId,
        origin,
        preview,
        inCombat: _inCombat(widget),
        candidateCount: _candidates.length,
    });

    if (_candidates.length === 0) {
        if (_prevThreatIds.size) {
            for (const id of _prevThreatIds) _decorate(id, false);
            _prevThreatIds = new Set();
        }
        _renderThreatLines(widget, [], preview);
        return { threatActive: false, snap: null };
    }

    const next = new Set<string>();
    const threatList: ThreatCandidate[] = [];
    for (const cand of _candidates) {
        if (_threatensMover(widget, cand.creatureObj, moverObj, origin, preview)) {
            next.add(cand.id);
            threatList.push(cand);
        }
    }

    // Add / re-decorate newly-threatened (idempotent; self-heals after WS render).
    for (const id of next) _decorate(id, true);
    // Remove no-longer-threatened.
    for (const id of _prevThreatIds) {
        if (!next.has(id)) _decorate(id, false);
    }
    _prevThreatIds = next;

    // BG3-style threat lines from each hostile to the mover preview.
    _renderThreatLines(widget, threatList, preview);

    if (next.size) _dbg('threats', { ids: [...next] });

    const snap = next.size ? _computeSnap(widget, moverObj, origin, preview) : null;
    return { threatActive: next.size > 0, snap };
}

function clear(): void {
    if (_prevThreatIds.size) {
        for (const id of _prevThreatIds) _decorate(id, false);
    }
    _prevThreatIds = new Set();
    _candidates = null;
    _candidatesForMoverId = null;
    _removeThreatSvg();
}

window.FT3D_OA_THREAT = { update, clear, computeCandidates };

_dbg('module init', { version: 2 });

export {};
